import { CUSTOMER_API_BASE_URL } from '../config/env';

export type TrellisJobStatus = 'queued' | 'generating' | 'validating' | 'storing' | 'ready' | 'failed' | 'cancelled';

export interface TrellisGeneratedAsset {
  assetId: string;
  label: string;
  glbUrl: string;
  gaussianPlyUrl?: string;
  source: 'trellis';
  suggestedDimensions?: { width?: number; depth?: number; length?: number };
}

export interface TrellisJob {
  jobId: string;
  status: TrellisJobStatus;
  progress: number;
  asset?: TrellisGeneratedAsset;
  error?: { code: string; message: string };
}

export async function getTrellisConfig(): Promise<{ configured: boolean; maxImageBytes: number; acceptedImageTypes: string[] }> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/trellis/config`, { credentials: 'include' });
  if (!response.ok) throw new Error('Could not read Trellis configuration');
  return response.json();
}

export async function createTrellisJob(file: File, sceneId: string, label: string): Promise<TrellisJob> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/trellis/jobs`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': file.type,
      'X-Scene-Id': sceneId,
      'X-Asset-Label': label,
      'X-File-Name': file.name,
    },
    body: file,
  });
  const payload = await response.json().catch(() => ({})) as TrellisJob & { error?: string | { message?: string } };
  if (!response.ok) {
    const message = typeof payload.error === 'string' ? payload.error : payload.error?.message;
    throw new Error(message ?? `Generation request failed (${response.status})`);
  }
  return payload;
}

export async function createTrellisJobFromCandidate(
  sceneId: string, label: string, fluxJobId: string, candidateId: string,
): Promise<TrellisJob> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/trellis/jobs/from-candidate`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sceneId, label, fluxJobId, candidateId }),
  });
  const payload = await response.json().catch(() => ({})) as TrellisJob & { error?: string | { message?: string } };
  if (!response.ok) {
    const message = typeof payload.error === 'string' ? payload.error : payload.error?.message;
    throw new Error(message ?? `3D generation request failed (${response.status})`);
  }
  return payload;
}

export async function getTrellisJob(jobId: string): Promise<TrellisJob> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/trellis/jobs/${encodeURIComponent(jobId)}`, { credentials: 'include' });
  if (!response.ok) throw new Error(`Could not read generation job (${response.status})`);
  return response.json();
}

export async function cancelTrellisJob(jobId: string): Promise<void> {
  await fetch(`${CUSTOMER_API_BASE_URL}/api/trellis/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE', credentials: 'include' });
}
